import { IcoCheckCircle, IcoCircle } from "../../../../components/icons";
import type { QuestionFrequency } from "./types";

export const QUESTION_CATEGORIES = [
  "AI & Innovation",
  "Team & People",
  "Projects & Results",
  "Growth & Support",
  "Leadership",
  "Wellbeing & Reflection",
  "Company & Culture",
] as const;

type FrequencySectionProps = {
  frequency: QuestionFrequency;
  category: string | null;
  onChange: (frequency: QuestionFrequency) => void;
  onCategoryChange: (category: string | null) => void;
  queuedCount?: number; // Number of other questions in the queue
};

export function FrequencySection({
  frequency,
  category,
  onChange,
  onCategoryChange,
  queuedCount = 0,
}: FrequencySectionProps) {
  return (
    <fieldset class="flex flex-col gap-3">
      <legend class="text-sm font-medium text-text-secondary">
        When should this question appear?
      </legend>

      <div class="flex flex-col gap-2 mt-1">
        <FrequencyOption
          name="frequency"
          value="every"
          checked={frequency === "every"}
          onChange={() => onChange("every")}
          label="Every check-in"
          description="Always included"
        />
        <FrequencyOption
          name="frequency"
          value="queued"
          checked={frequency === "queued"}
          onChange={() => onChange("queued")}
          label="In rotation"
          description={
            queuedCount > 0
              ? `Takes turns with ${queuedCount} other question${queuedCount === 1 ? "" : "s"}`
              : "Add to the rotating question queue"
          }
        />
        <FrequencyOption
          name="frequency"
          value="one_time"
          checked={frequency === "one_time"}
          onChange={() => onChange("one_time")}
          label="One time only"
          description="Shows once per person, then stops"
        />
      </div>

      {frequency === "queued" && (
        <div class="flex flex-col gap-1 ml-8">
          <label class="text-sm font-medium text-text-secondary">
            Category
          </label>
          <select
            value={category ?? ""}
            onChange={(e) => {
              const val = (e.target as HTMLSelectElement).value;
              onCategoryChange(val || null);
            }}
            class="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text hover:border-text-subtle focus:outline-none focus:ring-2 focus:ring-ring transition-colors"
          >
            <option value="">No category</option>
            {QUESTION_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <p class="text-xs text-text-muted">
            Each check-in pulls at most one question per category
          </p>
        </div>
      )}
    </fieldset>
  );
}

// ============================================================================
// Sub-components
// ============================================================================

type FrequencyOptionProps = {
  name: string;
  value: string;
  checked: boolean;
  onChange: () => void;
  label: string;
  description: string;
};

function FrequencyOption({
  name,
  value,
  checked,
  onChange,
  label,
  description,
}: FrequencyOptionProps) {
  return (
    <label
      class={`flex gap-3 items-start rounded-lg border p-3 transition-colors cursor-pointer focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:ring-offset-ring-offset ${
        checked
          ? "border-text-subtle bg-surface"
          : "border-border hover:border-text-subtle"
      }`}
    >
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        onChange={onChange}
        class="sr-only"
      />
      <div class={`mt-0.5 ${checked ? "text-text" : "text-text-subtle"}`}>
        {checked ? (
          <IcoCheckCircle class="size-5" />
        ) : (
          <IcoCircle class="size-5" />
        )}
      </div>
      <span class="flex flex-col gap-0.5">
        <span class="font-medium text-text">{label}</span>
        <span class="text-sm text-text-muted">{description}</span>
      </span>
    </label>
  );
}
